import axios from 'axios';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Helper function to get the current token from localStorage
const getToken = () => localStorage.getItem('token');

// Helper function to get the refresh token from localStorage
const getRefreshToken = () => localStorage.getItem('refreshToken');

// Helper function to update tokens in localStorage
const updateTokens = (accessToken, refreshToken, expiresAt) => {
  localStorage.setItem('token', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('expiresAt', expiresAt);
};


// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.getItem('token'),
  },
});
// Function to refresh the access token
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('refresh token', refreshToken);

    const response = await api.post('/auth/refresh-token', { refreshToken, userType: 'consumer' });
    const { accessToken, refreshToken: newRefreshToken, expiresAt } = response.data;
    updateTokens(accessToken, newRefreshToken, expiresAt);
    return accessToken;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    throw error;
  }
};

// Axios interceptor for handling token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await refreshAccessToken();
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout the user
        logoutAndRedirect();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Add this interceptor
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    const expiresAt = localStorage.getItem('expiresAt');

    if (token && expiresAt) {
      if (new Date() >= new Date(expiresAt)) {
        // Token has expired
        console.log('Token expired, logging out');
        logoutAndRedirect();
        throw new axios.Cancel('Session expired');
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Implement this function
const logoutAndRedirect = () => {
  localStorage.removeItem('customerId');
  localStorage.removeItem('customerEmail');
  localStorage.removeItem('customerProfile');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('expiresAt');
  window.location.href = '/login';
};

export const logoutUser = async () => {
  try {
    const response = await api.post('/auth/logout');
    return response.data;
  } catch (error) {
    console.error('Error during logout:', error);
    throw error;
  } finally {
    logoutAndRedirect();
  }
};

export const fetchCustomerProfile = async (customerId) => {
  try {
    const response = await api.get(`/customer/${customerId}/profile`);
    return response.data;
  } catch (error) {
    console.error('Error fetching customer profile:', error);
    throw error;
  }
};

export const fetchBusinessData = async (businessId) => {
  try {
    const response = await api.get(`/business/${businessId}/basic-data`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business data:', error);
    throw error;
  }
};

export const checkUserReviews = async (email, businessId) => {
  // Implement API call to check user reviews
  // Return an object with totalReviews and businessReviews
  return { totalReviews: 0, businessReviews: 0, ratingForNextMilestone: 0 };
};

export const checkUserAccount = async (email) => {
  try {
    const response = await api.post('/customer/check-customer', { email });
    return response.data;
  } catch (error) {
    console.error('Error checking user account:', error);
    throw error;
  }
};

export const createAccount = async (email, password) => {
  try {
    const userAccountResponse = await api.post('/customer/create-account', { email, password });

    

    return userAccountResponse.data;
    
  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const response = await api.post('/customer-auth/login', { email, password });
    const { token, refreshToken, expiresAt, userId, sessionTimeout } = response.data;
    updateTokens(token, refreshToken, expiresAt);
    //api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return { token, refreshToken, expiresAt, userId, sessionTimeout };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const submitReview = async (reviewData) => {
  try {
    const response = await api.post('/reviews', reviewData);
    return response.data;
  } catch (error) {
    console.error('Error submitting review:', error);
    throw error;
  }
};

export const submitFeedback = async (feedbackData, businessId) => {
  try {
    const response = await api.post(`/business/submit-review/${businessId}`, feedbackData);
    return response.data;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

export const updateCustomerProfile = async (customerId, updatedProfile) => {
  try {
    const response = await api.put(`/customer/${customerId}/update-profile`, updatedProfile);
    return response.data;
  } catch (error) {
    console.error('Error updating customer profile:', error);
    throw error;
  }
};

export const createCustomerProfile = async (customerId, profileData) => {
  try {
    const response = await api.post(`/customer/${customerId}/create-profile`, profileData);
    return response.data;
  } catch (error) {
    console.error('Error creating customer profile:', error);
    throw error;
  }
};

export const fetchUserEmail = async (customerId) => {
  try {
    const response = await api.get(`/customer/${customerId}/email`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user email:', error);
    throw error;
  }
};

export const changePassword = async (customerId, currentPassword, newPassword) => {
  try {
    const response = await api.post(`/customer/${customerId}/change-password`, {
      currentPassword,
      newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

// Set up the authorization header for all requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      //config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    console.error('Error in request:', error);
    return Promise.reject(error);
  }
);

export default api;